import axios from "axios";
import { action, makeObservable, observable, runInAction } from "mobx";
import { APIBlobResponse } from "../../../../models/NetworkResponse";
import AlertsDashboardAPI from "../../api/dashboards/AlertsDashboardAPI";
import {
  ALERTS_DRILLDOWN_SORT,
  ALERTS_EMAILS_SORT,
  ALERTS_PAGE_SIZE,
  ALERTS_PUSH_SORT,
  ALERTS_RECIPIENT_SORT,
  AlertsAppliedFilters,
  AlertsDrilldownRequest,
  AlertsEmailListRequest,
  AlertsPushListRequest,
  AlertsRecipientRequest,
  AlertsSort,
  defaultAlertsAppliedFilters,
  EmailBatchStatus,
  PushStatus,
  RecipientKind,
  toAlertFilters,
  toAlertListFilters,
} from "../../../../models/Dashboard/AlertsDashboard/AlertsDashboardFilters";
import {
  AlertsAlertDetailResult,
  AlertsDrilldownResult,
  AlertsEmailBatchDetailResult,
  AlertsListResult,
  AlertsRecipientResult,
} from "../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import {
  AlertsCardList,
  AlertsMetricInfo,
  AlertsSummaryResult,
} from "../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";
import {
  AlertsDashboardTrackExtra,
  trackAlertsDashboardAction,
} from "../../../../analytics/alertsDashboardTracking";
import { AlertsDashboardActionId } from "../../../../analytics/catalog/alertsDashboardActions";

export type AlertsDashboardTab = "overview" | "emails" | "push";

export type AlertsListKind = "drilldown" | "emails" | "push" | "recipient";

export type AlertsListPayload =
  AlertsDrilldownResult | AlertsListResult | AlertsRecipientResult;

/** A paged, sortable table view (drill-down, emails, push or recipient). */
export interface AlertsListView {
  id: number;
  type: "list";
  kind: AlertsListKind;
  title: string;
  kicker: string;
  /** `how` of the card / row that opened the view (shown until the payload arrives). */
  how: AlertsMetricInfo | null;
  metricId?: string;
  /** Row statuses for `/emails` (batch) and `/push` (delivery). */
  statuses: string[];
  recipient?: { kind: RecipientKind; id: number; name: string | null };
  page: number;
  /** `null` → server default; the echoed `table.sort` is shown. */
  sort: AlertsSort | null;
  result: AlertsListPayload | null;
  loading: boolean;
  error: string | null;
  exportLoading: boolean;
  exportError: string | null;
}

export interface AlertsAlertView {
  id: number;
  type: "alert";
  alertId: number;
  title: string;
  result: AlertsAlertDetailResult | null;
  loading: boolean;
  error: string | null;
}

export interface AlertsEmailView {
  id: number;
  type: "email";
  batchId: number;
  title: string;
  result: AlertsEmailBatchDetailResult | null;
  loading: boolean;
  error: string | null;
}

export type AlertsDrawerView =
  AlertsListView | AlertsAlertView | AlertsEmailView;

export interface AlertsKnownType {
  type_code: string;
  audience: string;
  section_code: string;
  section_label: string | null;
}

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (data instanceof Blob) {
      return fallback;
    }
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string" &&
      data.message
    ) {
      return data.message;
    }
    if (error.message) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

const triggerCsvDownload = (blob: Blob, filename?: string) => {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename || "alerts-export.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(downloadUrl);
};

/** Sort whitelist for a list view (contract §2, §4, §6, §7). */
export const sortWhitelistForView = (
  view: AlertsListView,
): readonly string[] => {
  if (view.kind === "emails") {
    return ALERTS_EMAILS_SORT;
  }
  if (view.kind === "push") {
    return ALERTS_PUSH_SORT;
  }
  if (view.kind === "recipient" && view.recipient) {
    return ALERTS_RECIPIENT_SORT[view.recipient.kind];
  }
  return ALERTS_DRILLDOWN_SORT;
};

const LIST_KICKERS: Record<AlertsListKind, string> = {
  drilldown: "Alerts drill-down",
  emails: "Client alert emails",
  push: "Admin push deliveries",
  recipient: "Recipient",
};

export class AlertsDashboardStore {
  analyticsAPI: AlertsDashboardAPI;
  appliedFilters: AlertsAppliedFilters = defaultAlertsAppliedFilters();

  summaryLoading = false;
  summaryError: string | null = null;
  summaryResult: AlertsSummaryResult | null = null;

  tab: AlertsDashboardTab = "overview";
  /** Inline Emails / Push list for the Emails and Push tabs. */
  tabList: AlertsListView | null = null;

  /** Drawer navigation stack; the last entry is visible. Empty → closed. */
  drawerViews: AlertsDrawerView[] = [];

  /** Every type / section seen in `by_type` (there is no filters endpoint). */
  knownTypes: AlertsKnownType[] = [];
  /** Customer names seen in `by_customer`, for hydrating URL ids. */
  knownCustomers: Record<string, string> = {};

  private summarySeq = 0;
  private viewSeq = 0;
  private requestSeq = new Map<number, number>();

  constructor() {
    this.analyticsAPI = new AlertsDashboardAPI();
    makeObservable(this, {
      appliedFilters: observable.ref,
      summaryLoading: observable,
      summaryError: observable,
      summaryResult: observable.ref,
      tab: observable,
      tabList: observable.ref,
      drawerViews: observable.ref,
      knownTypes: observable.ref,
      knownCustomers: observable.ref,
      setAppliedFilters: action,
      fetchSummary: action,
      refreshDashboard: action,
      refreshData: action,
      changeTab: action,
      openCard: action,
      openDrilldown: action,
      openList: action,
      openRecipient: action,
      openAlert: action,
      openEmail: action,
      goBack: action,
      closeDrawer: action,
      changeListStatuses: action,
      changePage: action,
      changeSort: action,
      fetchView: action,
      exportCsv: action,
    });
  }

  get drawerOpen(): boolean {
    return this.drawerViews.length > 0;
  }

  get drawerTop(): AlertsDrawerView | null {
    return this.drawerViews[this.drawerViews.length - 1] ?? null;
  }

  track = (
    actionId: AlertsDashboardActionId,
    extra?: AlertsDashboardTrackExtra,
  ) => {
    trackAlertsDashboardAction(actionId, this.appliedFilters, extra);
  };

  setAppliedFilters = (filters: AlertsAppliedFilters) => {
    this.appliedFilters = filters;
  };

  private rememberOptions = (result: AlertsSummaryResult) => {
    try {
      const types = new Map(
        this.knownTypes.map((item) => [item.type_code, item]),
      );
      (result.data.by_type?.rows ?? []).forEach((row) => {
        types.set(row.type_code, {
          type_code: row.type_code,
          audience: row.audience,
          section_code: row.section_code,
          section_label: row.section_label,
        });
      });
      const customers = { ...this.knownCustomers };
      (result.data.by_customer?.rows ?? []).forEach((row) => {
        if (row.customer_name) {
          customers[String(row.customer_id)] = row.customer_name;
        }
      });
      this.knownTypes = Array.from(types.values()).sort((a, b) =>
        a.type_code.localeCompare(b.type_code),
      );
      this.knownCustomers = customers;
    } catch (error) {
      console.error(error);
    }
  };

  fetchSummary = async (filters?: AlertsAppliedFilters) => {
    const requestId = ++this.summarySeq;
    try {
      if (filters) {
        this.appliedFilters = filters;
      }
      this.summaryLoading = true;
      this.summaryError = null;

      const result = await this.analyticsAPI.getSummary(
        toAlertFilters(this.appliedFilters),
      );
      if (requestId !== this.summarySeq) {
        return;
      }
      runInAction(() => {
        this.summaryResult = result;
        this.rememberOptions(result);
      });
    } catch (error) {
      if (requestId !== this.summarySeq) {
        return;
      }
      runInAction(() => {
        this.summaryError = getApiErrorMessage(
          error,
          "Failed to fetch alerts summary.",
        );
        this.summaryResult = null;
      });
    } finally {
      if (requestId === this.summarySeq) {
        runInAction(() => {
          this.summaryLoading = false;
        });
      }
    }
  };

  /** New filters: summary, the tab list and the visible drawer list all go back to page 1. */
  refreshDashboard = async (filters?: AlertsAppliedFilters) => {
    try {
      const jobs: Array<Promise<void>> = [this.fetchSummary(filters)];
      if (this.tabList) {
        this.patchView(this.tabList.id, { page: 1 });
        jobs.push(this.fetchView(this.tabList.id));
      }
      const top = this.drawerTop;
      if (top?.type === "list") {
        this.patchView(top.id, { page: 1 });
        jobs.push(this.fetchView(top.id));
      }
      await Promise.all(jobs);
    } catch (error) {
      throw error;
    }
  };

  /** Reload with the applied filters, keeping page / sort. */
  refreshData = async () => {
    try {
      const jobs: Array<Promise<void>> = [this.fetchSummary()];
      if (this.tabList) {
        jobs.push(this.fetchView(this.tabList.id));
      }
      const top = this.drawerTop;
      if (top) {
        this.track("drawer_refresh", {
          view: this.viewKind(top),
          title: top.title,
        });
        jobs.push(this.fetchView(top.id));
      } else {
        this.track("dashboard_refresh");
      }
      await Promise.all(jobs);
    } catch (error) {
      throw error;
    }
  };

  changeTab = async (tab: AlertsDashboardTab) => {
    try {
      if (this.tab === tab) {
        return;
      }
      this.track("tab_change", { tab });
      this.tab = tab;
      if (tab === "overview") {
        this.tabList = null;
        return;
      }
      const view = this.newListView(tab, {
        title:
          tab === "emails" ? "Client alert emails" : "Admin push deliveries",
        how: null,
        statuses: [],
      });
      this.tabList = view;
      await this.fetchView(view.id);
    } catch (error) {
      throw error;
    }
  };

  private newListView = (
    kind: AlertsListKind,
    init: Pick<AlertsListView, "title" | "how" | "statuses"> &
      Partial<Pick<AlertsListView, "metricId" | "recipient">>,
  ): AlertsListView => ({
    id: ++this.viewSeq,
    type: "list",
    kind,
    kicker: LIST_KICKERS[kind],
    page: 1,
    sort: null,
    result: null,
    loading: true,
    error: null,
    exportLoading: false,
    exportError: null,
    ...init,
  });

  private pushView = async (view: AlertsDrawerView, reset: boolean) => {
    this.drawerViews = reset ? [view] : [...this.drawerViews, view];
    await this.fetchView(view.id);
  };

  /** Summary card click: push/email cards open their delivery list, others the drill-down. */
  openCard = async (card: {
    metricId: string;
    label: string;
    how: AlertsMetricInfo;
    list: AlertsCardList | null;
  }) => {
    try {
      if (card.list) {
        this.track("card_list_click", {
          view: card.list.endpoint === "/push" ? "push" : "emails",
          title: card.label,
          metricId: card.metricId,
          statuses: card.list.statuses,
        });
        await this.openList(
          card.list.endpoint === "/push" ? "push" : "emails",
          card.list.statuses,
          card.label,
          card.how,
          true,
        );
        return;
      }
      this.track("card_click", {
        view: "drilldown",
        title: card.label,
        metricId: card.metricId,
      });
      await this.openDrilldown(card.metricId, card.label, card.how);
    } catch (error) {
      throw error;
    }
  };

  /** `POST /drilldown/:metricId` with the applied filters. Starts a new drawer stack. */
  openDrilldown = async (
    metricId: string,
    title: string,
    how: AlertsMetricInfo | null,
    source?: AlertsDashboardActionId,
  ) => {
    try {
      if (source) {
        this.track(source, { view: "drilldown", title, metricId });
      }
      const view = this.newListView("drilldown", {
        title,
        how,
        metricId,
        statuses: [],
      });
      await this.pushView(view, true);
    } catch (error) {
      throw error;
    }
  };

  /** `/emails` or `/push` list in the drawer, pre-filtered to `statuses`. */
  openList = async (
    kind: "emails" | "push",
    statuses: string[],
    title: string,
    how: AlertsMetricInfo | null,
    reset: boolean,
  ) => {
    try {
      const view = this.newListView(kind, {
        title,
        how,
        statuses: [...statuses],
      });
      await this.pushView(view, reset);
    } catch (error) {
      throw error;
    }
  };

  /** `POST /recipient` — everything one admin / client user received in the range. */
  openRecipient = async (
    kind: RecipientKind,
    id: number,
    name: string | null,
  ) => {
    try {
      const title =
        name?.trim() || `${kind === "ADMIN" ? "Admin" : "User"} #${id}`;
      this.track("recipient_open", { view: "recipient", title });
      const view = this.newListView("recipient", {
        title,
        how: null,
        statuses: [],
        recipient: { kind, id, name },
      });
      await this.pushView(view, !this.drawerOpen);
    } catch (error) {
      throw error;
    }
  };

  openAlert = async (alertId: number, title?: string | null) => {
    try {
      if (!Number.isInteger(alertId) || alertId <= 0) {
        return;
      }
      const label = title?.trim() || `Alert #${alertId}`;
      this.track("alert_open", { view: "alert", title: label });
      const view: AlertsAlertView = {
        id: ++this.viewSeq,
        type: "alert",
        alertId,
        title: label,
        result: null,
        loading: true,
        error: null,
      };
      await this.pushView(view, !this.drawerOpen);
    } catch (error) {
      throw error;
    }
  };

  openEmail = async (batchId: number) => {
    try {
      if (!Number.isInteger(batchId) || batchId <= 0) {
        return;
      }
      const title = `Email #${batchId}`;
      this.track("email_open", { view: "email", title });
      const view: AlertsEmailView = {
        id: ++this.viewSeq,
        type: "email",
        batchId,
        title,
        result: null,
        loading: true,
        error: null,
      };
      await this.pushView(view, !this.drawerOpen);
    } catch (error) {
      throw error;
    }
  };

  goBack = async () => {
    try {
      const top = this.drawerTop;
      if (!top) {
        return;
      }
      this.track("drawer_back", { view: this.viewKind(top), title: top.title });
      this.requestSeq.delete(top.id);
      this.drawerViews = this.drawerViews.slice(0, -1);
      const next = this.drawerTop;
      // Filters may have changed while the view was hidden; reload it (server-cached).
      if (next?.type === "list") {
        await this.fetchView(next.id);
      }
    } catch (error) {
      throw error;
    }
  };

  closeDrawer = () => {
    const top = this.drawerTop;
    if (top) {
      this.track("drawer_close", {
        view: this.viewKind(top),
        title: top.title,
      });
    }
    this.drawerViews.forEach((view) => this.requestSeq.delete(view.id));
    this.drawerViews = [];
  };

  viewKind = (view: AlertsDrawerView): string =>
    view.type === "list" ? view.kind : view.type;

  findView = (id: number): AlertsDrawerView | null => {
    if (this.tabList?.id === id) {
      return this.tabList;
    }
    return this.drawerViews.find((view) => view.id === id) ?? null;
  };

  private patchView = (id: number, patch: Partial<AlertsDrawerView>) => {
    if (this.tabList?.id === id) {
      this.tabList = { ...this.tabList, ...patch } as AlertsListView;
      return;
    }
    if (!this.drawerViews.some((view) => view.id === id)) {
      return;
    }
    this.drawerViews = this.drawerViews.map((view) =>
      view.id === id ? ({ ...view, ...patch } as AlertsDrawerView) : view,
    );
  };

  private findListView = (id: number): AlertsListView | null => {
    const view = this.findView(id);
    return view?.type === "list" ? view : null;
  };

  changeListStatuses = async (id: number, statuses: string[]) => {
    try {
      const view = this.findListView(id);
      if (!view) {
        return;
      }
      this.track("list_status_change", {
        view: view.kind,
        title: view.title,
        statuses,
      });
      this.patchView(id, { statuses, page: 1 });
      await this.fetchView(id);
    } catch (error) {
      throw error;
    }
  };

  changePage = async (id: number, page: number) => {
    try {
      const view = this.findListView(id);
      if (!view) {
        return;
      }
      const nextPage = Math.max(1, page);
      this.track("table_page_change", {
        view: view.kind,
        title: view.title,
        page: nextPage,
        pageSize: ALERTS_PAGE_SIZE,
      });
      this.patchView(id, { page: nextPage });
      await this.fetchView(id);
    } catch (error) {
      throw error;
    }
  };

  changeSort = async (id: number, column: string) => {
    try {
      const view = this.findListView(id);
      if (!view || !sortWhitelistForView(view).includes(column)) {
        return;
      }
      const current = view.sort ?? view.result?.data.table.sort ?? null;
      const dir =
        current?.column === column && current.dir === "desc" ? "asc" : "desc";
      this.track("table_sort", {
        view: view.kind,
        title: view.title,
        sortColumn: column,
        sortDir: dir,
      });
      this.patchView(id, { sort: { column, dir }, page: 1 });
      await this.fetchView(id);
    } catch (error) {
      throw error;
    }
  };

  private listRequest = (
    view: AlertsListView,
    paged: boolean,
  ):
    | { kind: "drilldown"; body: AlertsDrilldownRequest }
    | { kind: "emails"; body: AlertsEmailListRequest }
    | { kind: "push"; body: AlertsPushListRequest }
    | { kind: "recipient"; body: AlertsRecipientRequest } => {
    const sort =
      view.sort && sortWhitelistForView(view).includes(view.sort.column)
        ? view.sort
        : undefined;
    const extras = {
      ...(paged
        ? { page: view.page, page_size: ALERTS_PAGE_SIZE }
        : {}),
      ...(sort ? { sort } : {}),
    };
    if (view.kind === "emails") {
      return {
        kind: "emails",
        body: {
          ...toAlertListFilters(this.appliedFilters),
          ...extras,
          ...(view.statuses.length > 0
            ? { statuses: view.statuses as EmailBatchStatus[] }
            : {}),
        },
      };
    }
    if (view.kind === "push") {
      return {
        kind: "push",
        body: {
          ...toAlertListFilters(this.appliedFilters),
          ...extras,
          ...(view.statuses.length > 0
            ? { statuses: view.statuses as PushStatus[] }
            : {}),
        },
      };
    }
    if (view.kind === "recipient" && view.recipient) {
      return {
        kind: "recipient",
        body: {
          ...toAlertFilters(this.appliedFilters),
          ...extras,
          kind: view.recipient.kind,
          id: view.recipient.id,
        },
      };
    }
    return {
      kind: "drilldown",
      body: { ...toAlertFilters(this.appliedFilters), ...extras },
    };
  };

  private loadView = async (
    view: AlertsDrawerView,
  ): Promise<Partial<AlertsDrawerView>> => {
    if (view.type === "alert") {
      const result = await this.analyticsAPI.getAlertDetail(view.alertId);
      return {
        result,
        title: result.alert?.title || view.title,
      } as Partial<AlertsAlertView>;
    }
    if (view.type === "email") {
      const result = await this.analyticsAPI.getEmailBatch(view.batchId);
      return { result } as Partial<AlertsEmailView>;
    }
    const request = this.listRequest(view, true);
    let result: AlertsListPayload;
    if (request.kind === "emails") {
      result = await this.analyticsAPI.getEmails(request.body);
    } else if (request.kind === "push") {
      result = await this.analyticsAPI.getPush(request.body);
    } else if (request.kind === "recipient") {
      result = await this.analyticsAPI.getRecipient(request.body);
    } else {
      result = await this.analyticsAPI.getDrilldown(
        view.metricId || "alerts:raised",
        request.body,
      );
    }
    return { result } as Partial<AlertsListView>;
  };

  /** Loads (or reloads) one view. Stale responses are dropped per view. */
  fetchView = async (id: number) => {
    const view = this.findView(id);
    if (!view) {
      return;
    }
    const requestId = (this.requestSeq.get(id) ?? 0) + 1;
    this.requestSeq.set(id, requestId);
    try {
      this.patchView(id, { loading: true, error: null });
      const patch = await this.loadView(view);
      if (this.requestSeq.get(id) !== requestId) {
        return;
      }
      runInAction(() => {
        this.patchView(id, patch);
      });
    } catch (error) {
      if (this.requestSeq.get(id) !== requestId) {
        return;
      }
      runInAction(() => {
        this.patchView(id, {
          error: getApiErrorMessage(error, "Failed to load alerts data."),
        });
      });
    } finally {
      if (this.requestSeq.get(id) === requestId) {
        runInAction(() => {
          this.patchView(id, { loading: false });
        });
      }
    }
  };

  exportCsv = async (id: number, fallbackName: string) => {
    const view = this.findListView(id);
    if (!view) {
      return;
    }
    try {
      this.track("export_csv", {
        view: view.kind,
        title: view.title,
        context: {
          sort_column: view.sort?.column,
          sort_dir: view.sort?.dir,
        },
      });
      this.patchView(id, { exportLoading: true, exportError: null });

      const request = this.listRequest(view, false);
      let result: APIBlobResponse;
      if (request.kind === "emails") {
        result = await this.analyticsAPI.exportEmailsCsv(request.body);
      } else if (request.kind === "push") {
        result = await this.analyticsAPI.exportPushCsv(request.body);
      } else if (request.kind === "recipient") {
        result = await this.analyticsAPI.exportRecipientCsv(request.body);
      } else {
        result = await this.analyticsAPI.exportDrilldownCsv(
          view.metricId || "alerts:raised",
          request.body,
        );
      }
      if (!result.data) {
        throw new Error("Download failed");
      }
      triggerCsvDownload(result.data, result.filename || fallbackName);
    } catch (error) {
      runInAction(() => {
        this.patchView(id, {
          exportError: getApiErrorMessage(error, "Failed to export CSV."),
        });
      });
    } finally {
      runInAction(() => {
        this.patchView(id, { exportLoading: false });
      });
    }
  };
}
